<template>
  <div class="page-container">
    <el-row type="flex" :gutter="18" class="mb-3">
      <el-col :span="24" style="display: flex; justify-content: space-between;">
        <h2 class="ma-0" style="align-self: flex-start;">Blacklist Management</h2>
        <div class="actions">
          <el-button type="primary" size="small" icon="el-icon-plus" @click="showAddDialog">Add SSN</el-button>
          <el-button type="info" size="small" icon="el-icon-upload2" @click="showImportDialog">Import</el-button>
          <el-dropdown style="margin-left: 10px;" split-button type="success" size="small" @click="exportCsv"
            @command="handleExportCommand" :disabled="exporting">
            <i class="el-icon-download" /> Export
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="priorYear">Export Prior Year</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
          <el-popconfirm title="Are you sure you want to delete ALL entries from the blacklist?"
            confirm-button-text="Yes, Purge All" cancel-button-text="Cancel" icon="el-icon-warning" icon-color="red"
            @confirm="purgeAll">
            <el-button slot="reference" style="margin-left: 10px;" type="danger" size="small" icon="el-icon-delete"
              :loading="purging">Purge
              All</el-button>
          </el-popconfirm>
          <el-input v-model="searchQuery" placeholder="Search SSN..." prefix-icon="el-icon-search" size="small"
            clearable style="width: 200px; margin-left: 10px;" />
        </div>
      </el-col>
    </el-row>
    <el-card>
      <el-table :data="filteredEntries" v-loading="loading" stripe style="width: 100%" max-height="600">
        <el-table-column prop="ssn" label="SSN" />
        <el-table-column label="Actions" width="120">
          <template slot-scope="scope">
            <el-popconfirm title="Delete this SSN from the blacklist?" confirm-button-text="Delete"
              cancel-button-text="Cancel" @confirm="deleteEntry(scope.row.id)">
              <el-button slot="reference" type="danger" size="mini" icon="el-icon-delete" />
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>
      <div class="footer-info">
        Total entries: {{ entries.length }} | Showing: {{ filteredEntries.length }}
      </div>
    </el-card>

    <!-- Add SSN Dialog -->
    <el-dialog title="Add SSN to Blacklist" :visible.sync="addDialogVisible" width="400px">
      <el-form ref="addSsnForm" :model="addForm" :rules="rules" @submit.native.prevent="addSsn">
        <el-form-item label="SSN" prop="ssn">
          <masked-el-input v-model="addForm.ssn" :mask="'###-##-####'" placeholder="Social Security Number"
            ref="ssnInput" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="addDialogVisible = false">Cancel</el-button>
        <el-button type="primary" @click="addSsn" :loading="adding">Add</el-button>
      </span>
    </el-dialog>

    <!-- Import Dialog -->
    <el-dialog title="Import Blacklist" :visible.sync="importDialogVisible" width="500px">
      <el-form>
        <el-form-item label="Import Mode">
          <el-radio-group v-model="importMode">
            <el-radio label="add">Add to existing (skip duplicates)</el-radio><br />
            <el-radio label="remove">Remove matching entries</el-radio><br />
            <el-radio label="replace">Replace all (purge existing first)</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="CSV File">
          <el-upload ref="upload" action="" :auto-upload="false" :limit="1" :on-change="handleFileChange"
            :on-remove="handleFileRemove" accept=".csv" drag>
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">Drop CSV file here or <em>click to select</em></div>
            <div class="el-upload__tip" slot="tip">CSV file with one SSN per row</div>
          </el-upload>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="importDialogVisible = false">Cancel</el-button>
        <el-button :type="importMode === 'remove' ? 'danger' : 'primary'" @click="importFile" :loading="importing"
          :disabled="!selectedFile">{{ importMode === 'remove' ? 'Remove' : 'Import' }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import api from '@/api';
import MaskedElInput from '@/components/form-item/MaskedElInput.vue';
import { TAX_YEAR } from '@/enum/TaxYear';
import { Required, SSN } from '@/util/formValidators';

export default {
  components: {
    MaskedElInput,
  },
  data() {
    return {
      entries: [],
      loading: false,
      searchQuery: '',
      addDialogVisible: false,
      addForm: {
        ssn: '',
      },
      adding: false,
      importDialogVisible: false,
      importMode: 'add',
      selectedFile: null,
      importing: false,
      exporting: false,
      purging: false,
      rules: {
        ssn: [Required, SSN],
      },
    };
  },
  computed: {
    filteredEntries() {
      if (!this.searchQuery) {
        return this.entries;
      }
      const query = this.searchQuery.toLowerCase().replace(/[-\s]/g, '');
      return this.entries.filter((entry) => {
        const ssn = entry.ssn.toLowerCase().replace(/[-\s]/g, '');
        return ssn.includes(query);
      });
    },
  },
  mounted() {
    this.loadEntries();
  },
  methods: {
    async loadEntries() {
      this.loading = true;
      try {
        const response = await api.blacklist.getAll();
        this.entries = response.data || [];
      } catch (error) {
        this.$message.error('Failed to load blacklist entries');
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    showAddDialog() {
      this.addForm.ssn = '';
      this.addDialogVisible = true;
      this.$nextTick(() => {
        if (this.$refs.addSsnForm) {
          this.$refs.addSsnForm.clearValidate();
        }
        if (this.$refs.ssnInput) {
          this.$refs.ssnInput.focus();
        }
      });
    },
    async addSsn() {
      this.$refs.addSsnForm.validate(async (valid) => {
        if (!valid) {
          return;
        }
        this.adding = true;
        try {
          const response = await api.blacklist.add(this.addForm.ssn.trim());
          this.$message.success(response.data.message);
          this.addDialogVisible = false;
          this.loadEntries();
        } catch (error) {
          this.$message.error('Failed to add SSN');
          console.error(error);
        } finally {
          this.adding = false;
        }
      });
    },
    async deleteEntry(id) {
      try {
        await api.blacklist.remove(id);
        this.$message.success('Entry deleted');
        this.loadEntries();
      } catch (error) {
        this.$message.error('Failed to delete entry');
        console.error(error);
      }
    },
    async purgeAll() {
      this.purging = true;
      try {
        await api.blacklist.purge();
        this.$message.success('Blacklist purged');
        this.loadEntries();
      } catch (error) {
        this.$message.error('Failed to purge blacklist');
        console.error(error);
      } finally {
        this.purging = false;
      }
    },
    showImportDialog() {
      this.importMode = 'add';
      this.selectedFile = null;
      this.importDialogVisible = true;
      this.$nextTick(() => {
        if (this.$refs.upload) {
          this.$refs.upload.clearFiles();
        }
      });
    },
    handleFileChange(file) {
      this.selectedFile = file.raw;
    },
    handleFileRemove() {
      this.selectedFile = null;
    },
    async importFile() {
      if (!this.selectedFile) {
        this.$message.warning('Please select a file');
        return;
      }
      this.importing = true;
      try {
        const response = await api.blacklist.import(this.selectedFile, this.importMode);
        this.$message.success(response.data.message);
        this.importDialogVisible = false;
        this.loadEntries();
      } catch (error) {
        this.$message.error('Failed to import file');
        console.error(error);
      } finally {
        this.importing = false;
      }
    },
    async exportCsv() {
      this.exporting = true;
      try {
        const response = await api.blacklist.export();
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `TY${TAX_YEAR.CURRENT}_blacklist_export_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.$message.success('Export downloaded');
      } catch (error) {
        this.$message.error('Failed to export blacklist');
        console.error(error);
      } finally {
        this.exporting = false;
      }
    },
    handleExportCommand(command) {
      if (command === 'priorYear') {
        this.exportPriorYearCsv();
      }
    },
    async exportPriorYearCsv() {
      this.exporting = true;
      try {
        const response = await api.blacklist.exportPriorYear();
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `TY${TAX_YEAR.PRIOR}_blacklist_export_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.$message.success('Prior year export downloaded');
      } catch (error) {
        this.$message.error('Failed to export prior year blacklist');
        console.error(error);
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.footer-info {
  margin-top: 15px;
  color: #909399;
  font-size: 14px;
}
</style>
